import { useState, useEffect } from 'react'
import { Box, Tab, Tabs, useTheme } from '@mui/material'
import {
    useServiceRequest,
    getBlankServiceRequestForm,
} from '../../hooks/useServiceRequest'
import { RootState } from '../../store'
import { useSelector } from 'react-redux'
import { SubmitNewRequest } from './SubmitNewRequest'
import {
    activeStatuses,
    MaintenanceRequestForm,
    ServiceRequest,
} from '../../types/serviceRequest'
import { ServiceRequests } from './ServiceRequests'
import { useLocation, useNavigate } from 'react-router-dom'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { MessageModal } from '../Message/MessageModal'

export const Maintenance = () => {
    const theme = useTheme()
    const location = useLocation()
    const navigate = useNavigate()
    const selectedLease = useSelector(
        (state: RootState) => state.lease.selectedLease,
    )
    const [activeTab, setActiveTab] = useState(() => {
        // Get tab from URL search params or default to "requests"
        const params = new URLSearchParams(location.search)
        return params.get('tab') || 'requests'
    })
    const [newRequest, setNewRequest] = useState<MaintenanceRequestForm>(
        getBlankServiceRequestForm(),
    )
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [tenantImage, setTenantImage] = useState<File | null>(null)

    const [selectedServiceRequestChannel, setSelectedServiceRequestChannel] =
        useState<number>(-1)
    const [disableMessaging, setDisableMessaging] = useState(false)

    // Integrate useServiceRequest hook
    const {
        isLoading,
        serviceRequestList,
        getServiceRequestList,
        createServiceRequest,
        getServiceRequestChannel,
        updateServiceRequestState
    } = useServiceRequest()

    // Fetch service requests on component mount
    useEffect(() => {
        getServiceRequestList()
    }, [])

    // Update URL when tab changes
    const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
        setActiveTab(value)
        navigate(`/maintenance?tab=${value}`, { replace: true })
    }

    const handleSubmitRequest = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)

        // Create FormData for the request
        const formData = new FormData()
        if (selectedLease?.id) {
            formData.append('lease_id', selectedLease.id.toString())
        } else {
            console.error('No lease selected')
            setIsSubmitting(false)
            return
        }
        formData.append('title', newRequest.title)
        formData.append('description', newRequest.description)
        formData.append('priority', newRequest.priority)
        formData.append('category', newRequest.category)
        formData.append('location', newRequest.location)
        formData.append('preferred_time', newRequest.preferredTime)
        if (tenantImage) {
            formData.append('image', tenantImage)
        }

        try {
            await createServiceRequest(formData)
            await getServiceRequestList()
            setNewRequest(getBlankServiceRequestForm())
            setActiveTab('requests')
            navigate('/maintenance?tab=requests', { replace: true })
        } catch (error) {
            console.error('Error creating service request:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const onClickServiceRequestChannel = (serviceRequest: ServiceRequest) => {
        if (serviceRequest.channel_id) {
            setSelectedServiceRequestChannel(serviceRequest.channel_id)
        } else {
            getServiceRequestChannel(serviceRequest).then((channel) => {
                setSelectedServiceRequestChannel(channel)
            })
        }
        if (
            serviceRequest.workorder?.status &&
            !activeStatuses.includes(serviceRequest.workorder.status)
        ) {
            setDisableMessaging(true)
        } else {
            setDisableMessaging(false)
        }
    }

    return (
        <ResponsivePageContainer
            title="Maintenance"
            subtitle="Submit requests and track maintenance status"
        >
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                    mb: 4,
                    '& .MuiTab-root': {
                        color: 'rgba(255,255,255,0.7)',
                        '&.Mui-selected': {
                            color: 'rgba(255,255,255,0.9)',
                        },
                    },
                    '& .MuiTabs-indicator': {
                        backgroundColor: theme.palette.primary.main,
                    },
                }}
            >
                <Tab value="requests" label="My Requests" />
                <Tab value="submit" label="Submit New" />
            </Tabs>

            {activeTab === 'requests' && (
                <ServiceRequests
                    serviceRequestList={serviceRequestList}
                    onClickChannel={onClickServiceRequestChannel}
                />
            )}

            {activeTab === 'submit' && (
                <SubmitNewRequest
                    handleSubmitRequest={handleSubmitRequest}
                    newRequest={newRequest}
                    setNewRequest={setNewRequest}
                    isSubmitting={isSubmitting}
                    setTenantImage={setTenantImage}
                    tenantImage={tenantImage}
                />
            )}

            <MessageModal
                open={selectedServiceRequestChannel !== -1}
                channelId={selectedServiceRequestChannel}
                onClose={() => setSelectedServiceRequestChannel(-1)}
                afterCreateMessage={() => {
                    const serviceRequest = serviceRequestList.find(
                        (serviceRequest) =>
                            serviceRequest.channel_id ===
                            selectedServiceRequestChannel,
                    )
                    if (serviceRequest) {
                        updateServiceRequestState({
                            ...serviceRequest,
                            message_count:
                                (serviceRequest.message_count ?? 0) + 1,
                            unread_count: 0,
                        })
                    }
                }}
                disableMessaging={disableMessaging}
            />
        </ResponsivePageContainer>
    )
}
